import { useTranslation } from "react-i18next";
import AddIcon from "@mui/icons-material/Add";
import {
  getPowerSlideLabel,
  ISlide,
} from "../../../models/interactions/PowerSlideHelper";
import { getPowerSlideTemplate } from "../../../models/interactions/PowerSlideTemplates";

interface PowerSlidePagerProps {
  slide: ISlide;
  index: number;
  getValue: (key: string) => string;
  onChangeTemplate: () => void;
  onAdd: () => void;
}

/**
 * The pill above the canvas: which slide is open, what template it uses, and a
 * way to add the next one. It is deliberately not a stepper - the on-canvas
 * arrows were dropped in favour of the rail plus the keyboard.
 */
export default function PowerSlidePager(props: PowerSlidePagerProps) {
  const { slide, index, getValue, onChangeTemplate, onAdd } = props;
  const { t } = useTranslation();

  const template = getPowerSlideTemplate(slide.templateId);
  const templateName = template
    ? t(`powerslide.templates.${template.locId}`, {
        defaultValue: template.name,
      })
    : slide.templateId;

  const raw = getPowerSlideLabel(slide, getValue);
  // The chip already names the template, so an untitled slide shows its
  // position instead of repeating it.
  const label = raw.isPlaceholder
    ? t("powerslide.pager.slideNumber", {
        defaultValue: `Slide ${index + 1}`,
        number: index + 1,
      })
    : raw.label;

  return (
    <div
      aria-label="powerslide-pager"
      className="flex shrink-0 items-center rounded-full bg-creator-surface py-[8px] pl-[18px] pr-[10px] shadow-[0px_2px_8px_0px_rgba(28,41,56,0.1)]"
    >
      <span className="max-w-[280px] truncate text-[13px] font-medium text-creator-text">
        {label}
      </span>
      {/* The chip names the template and is how you swap it - the only place a
          slide's template is visible while editing it. */}
      <button
        type="button"
        aria-label="powerslide-change-template"
        onClick={onChangeTemplate}
        className="ml-[9px] rounded-[6px] bg-creator-primary-background px-[8px] py-[3px] text-[9.5px] text-creator-text-sub hover:text-creator-primary"
      >
        {templateName}
      </button>
      <div className="ml-[12px] h-[18px] w-px bg-creator-border" />
      <button
        type="button"
        aria-label="powerslide-pager-add-slide"
        onClick={onAdd}
        className="ml-[6px] flex items-center gap-[6px] rounded-full py-[6px] pl-[10px] pr-[12px] text-[12.5px] font-semibold text-creator-primary hover:bg-creator-primary-washed"
      >
        <AddIcon style={{ width: 13, height: 13 }} />
        {t("powerslide.rail.addSlide", { defaultValue: "Add slide" })}
      </button>
    </div>
  );
}
